<template>
    <div
        class="element"
        :data-required="required"
        :class="{ TagField: true, [this.type] : true, error : error.error, success: !error.error && error.init, hidden: this.hidden }"
    >
        <labelElement v-bind="resolveProps('label', this)" :error="error" />
        <placeholderElement v-bind="resolveProps('placeholder', this)" :error="error" :value="this.input" />
        <stateElement v-bind="resolveProps('state', this)" :error="error" />

        <input type="hidden" ref="inputRef" :name="this.name" :value="inputValue" @change="handleChange">

        <div class="dropdown" :data-name="this.name" v-if="editing">
            <div class="input-group">
                <input
                    type="text"
                    class="input form-control"
                    :placeholder="`${this.show_placeholder ? placeholder + (required ? ' *' : '') : ''}`"
                    :value="searchValue"
                    ref="searchRef"
                    @click="handleSearchClick"
                    @focus="handleSearchFocus"
                    @blur="handleSearchBlur"
                    @input="handleSearchInput"
                >
                <div class="input-group-append input-group-btn input-group-toggle" v-if="editing && (!searchShowall || (searchShowall && componentData.loading))">
                    <div class="btn">
                        <button class="more" :disabled="searchShowall && componentData.loading" type="button" @click.prevent="handleDropdownShowall">
                            <span class="title" v-if="searchShowall && componentData.loading">{{ trans("Laden") }}...</span>
                            <span class="title" v-else="!searchShowall">{{ trans("Toon opties") }}</span>
                        </button>
                    </div>
                </div>
                <div class="input-group-append input-group-btn" v-if="!show_dropdown">
                    <button class="btn" type="button" @click.prevent>
                        <Icon name="search" />
                    </button>
                </div>
                <div class="input-group-append input-group-btn" v-if="searchValue && !searchFocus">
                    <button class="btn" type="button" @click.prevent="handleSearchReset">
                        <Icon name="close" />
                    </button>
                </div>
                <div class="input-group-append input-group-btn" v-if="show_dropdown">
                    <button class="btn" type="button" @click.prevent="handleDropdownHide">
                        <Icon name="up" />
                    </button>
                </div>
            </div>

            <div class="dropdown-menu show" v-if="show_dropdown" :class="{loading: componentData.loading}">
                <div class="inner">
                    <template v-if="searchShowall && componentData.loading && !componentData.options.length">
                        {{ trans("Aan het laden...") }}
                    </template>
                    <template v-else-if="!searchShowall && searchValue.length < 2">
                        {{ trans("Geef minimaal twee karakters in...") }}
                    </template>
                    <template v-else-if="searchValue && componentData.loading">
                        {{ trans("Aan het laden...") }}
                    </template>
                    <template v-else>
                        <ul class="block-grid one-up">
                            <li v-for="option in order(options)">
                                <div class="dropdown-item" :class="{active: isOptionActive(option)}" @click.prevent="handleSearchSelection(option)">
                                    <span class="title">{{ option.title }}</span>
                                    <Icon name="close"  v-if="isOptionActive(option)"/>
                                </div>
                            </li>
                            <li v-if="!componentData.options.length">
                                {{ trans("Geen opties gevonden.") }}
                            </li>
                        </ul>
                    </template>
                </div>
            </div>
        </div>

        <div class="tags" :class="{dragging: isDragging}">
            <div
                class="tag"
                draggable="true"
                @dragstart="dragStart"
                @dragover.stop="allowDrop"
                @drop="drop"
                v-for="(id, key) in inputArray"
                :key="`${id}-${key}`"
                :data-key="key"
            >
                <span class="title">
                    {{ current_option(id)?.title ?? "..." }}
                    <template v-if="key === 0 && this.primary_label">({{ trans(this.primary_label) }})</template>
                </span>
                <button type="button" class="nobg" @click.stop="handleOption({id: id})" v-if="editing">
                    <Icon name="close" />
                </button>
            </div>
        </div>

        <errorElement v-bind="resolveProps('error', this)" :error="error" />
        <tooltipElement v-bind="resolveProps('tooltip', this)" :inline="true" />
    </div>
</template>

<script>
import { defineComponent, ref, watch, reactive, computed, inject, onMounted, onUnmounted } from "vue";

// Our form helpers
import { postDataAsync, patchDataAsync, getDataAsync, getDataWithoutLimit } from "helpers/_api";
import useFieldValidation from "formbuilder/fields/_validation";
import { propDefaults, resolveProps } from "formbuilder/fields/_props";

// Elements
import labelElement from "formbuilder/elements/LabelElement.vue";
import stateElement from "formbuilder/elements/StateElement.vue";
import placeholderElement from "formbuilder/elements/PlaceholderElement.vue";
import errorElement from "formbuilder/elements/ErrorElement.vue";
import tooltipElement from "formbuilder/elements/TooltipElement.vue";

// Helpers
import { clone } from "helpers/_data";
import { observeElement } from "helpers/_observer";
import { mergeDeep } from "helpers/_objects";
import trans from "helpers/_translation";

// **Imported from second file's logic**
import { resolveData, formatData } from "helpers/_data";
import { apiHostname, config, env } from "config";

import Icon from "components/icon";
import { store_entity } from "store";
// import { company_ids } from "store/portal";

export default defineComponent({
    props: mergeDeep(
        propDefaults,
        {
            primary_label: {
                type: String,
                required: false,
                default: "primair"
            },
            hideable: {
                type: Boolean,
                required: false,
                default: false
            }
        }
    ),
    components: {
        labelElement,
        stateElement,
        placeholderElement,
        errorElement,
        tooltipElement,
        Icon
    },
    setup(props, { emit }) {
        // --- refs / state
        const inputRef = ref();
        const searchRef = ref();
        const inputArray = ref([]);
        const inputValue = computed(() => JSON.stringify(inputArray.value || []));
        const { registerValidator, validate, error } = useFieldValidation();

        // from second file (provided by parent)
        const getItems = inject("getItems", null);
        const resolveTitle = inject("resolveTitle", (item) => item?.title ?? "");

        const componentData = reactive({
            loading: true,
            detail_id: null,
            options: [],
            hidden: {},
            results: {},
            all: {}
        });
        const options = computed(() => {
            const tmpObject = {};
            const companyProperties = env("company_property_prefilled");
            componentData.options.filter(a => {
                // Temporary disabled since we don't have company_ids yet
                // if (companyProperties.includes(props.name) && company_ids.value.length && !company_ids.value.includes(`${a.id}`)) {
                //     return false;
                // }
                return true;
            }).forEach(item => {
                tmpObject[item.id] = item;
            });
            return {...tmpObject};
        });

        const usingFilterSource = computed(() => {
            // Treat props.options as "config" when it has a filters array (second file semantics)
            return props?.options && typeof props.options === "object" && Array.isArray(props.options.filters);
        });

        // --- option source A: plain object (original)
        const resolveOptions = (options) => {
            let out = options || {};
            if (!props.required) {
                out = {
                    ...out
                };
            }
            return out;
        };

        // --- option source B: fetched via filters (second file)
        const loadOptionsFromFilters = async () => {
            try {
                const filters = props.options?.filters ?? [];
                const payload = filters.map(f => JSON.stringify(f));
                const cards = await getDataAsync(`${apiHostname}/${config.value.workflowcard?.url}`, payload);
                const { data } = formatData(cards);

                // Map to { id: title } using resolveTitle if available
                const mapped = Object.entries(data || {}).reduce((acc, [id, item]) => {
                    acc[id] = resolveTitle(item);
                    return acc;
                }, {});

                componentData.options = resolveOptions(mapped);

                // Ensure currently selected values exist somewhere (either options or hidden)
                inputArray.value.forEach(async (id) => {
                    if (!(id in componentData.options) && !(id in componentData.hidden)) {
                        // Prefer getItems from the second file if provided
                        if (getItems) {
                            try {
                                const item = await getItems("workflowcard", id);
                                componentData.hidden[id] = item;
                            } catch (e) { /* swallow */ }
                        }
                    }
                });
            } catch (e) {
                // Fallback to empty list on error (do not throw)
                componentData.options = resolveOptions({});
            }
        };

        // Keep the original options watcher, but augment with filter-based loading
        watch(
            () => props.options,
            (options) => {
                if (usingFilterSource.value) {
                    // fetch from backend per the second file logic
                    loadOptionsFromFilters();
                } else {
                    // behave as original TagField (plain map)
                    componentData.options = resolveOptions(options);
                }
            },
            { immediate: true, deep: true }
        );

        // Handle incoming value <-> array
        watch(
            () => props.value,
            () => {
                if (Array.isArray(props.value)) {
                    inputArray.value = props.value ?? [];
                } else if ((typeof props.value === "string") && props.value.startsWith("[")) {
                    try {
                        inputArray.value = JSON.parse(props.value) || [];
                    } catch (e) {
                        inputArray.value = [];
                    }
                } else if (props.value) {
                    inputArray.value = [props.value];
                }

                // If using the "filters" source, make sure we can render selected items not in the current options
                if (usingFilterSource.value && getItems) {
                    inputArray.value.forEach(async (id) => {
                        if (!(id in componentData.options) && !(id in componentData.hidden)) {
                            try {
                                const item = await getItems("workflowcard", id);
                                componentData.hidden[id] = item;
                            } catch (e) { /* swallow */ }
                        }
                    });
                }
            },
            { immediate: true }
        );

        // Validation (unchanged)
        const required = computed(() => props.required);
        watch(
            () => props.required,
            () => { registerValidator(inputValue, props); },
            { immediate: true }
        );

        // If we got an initial value, validate instantly
        props.value && validate();

        const handleChange = () => {
            emit("value-change", {
                name: props.name,
                value: inputValue.value
            });
        };

        // --- search & dropdown logic (original)
        const searchValue = ref("");
        const current_match = computed(() => {
            const result = Object.entries(componentData.options).find(([key, value]) => {
                return value.toLowerCase() === searchValue.value?.toLowerCase();
            });
            return result ? { id: result[0], title: result[1] } : null;
        });

        const current_option = (value) => {
            return computed(() => {
                const optionTarget = Object.values(componentData.options).find(a => `${a.id}` === `${value}`);
                // Prefer option map
                if (optionTarget) return optionTarget;

                // If using filter source and getItems populated hidden, use that
                if (value in componentData.hidden) return componentData.hidden[value];

                // Otherwise fall back to original hidden fetch (options_object path)
                componentData.hidden[value] = {};
                getDataAsync(`${apiHostname}/${config.value.workflowcard.url}/${value}`).then((result) => {
                    // Try resolveTitle for consistency if object-like
                    componentData.hidden[value] = result.data;
                });
                return componentData.hidden[value];
            }).value;
        };


        const optionsJson = computed(() => {
            const result = JSON.parse(props.options_json) ?? {
                filters: []
            };
            if (searchValue.value) {
                result.filters.push({"t": "SearchFilter", "c": "title", "v": searchValue.value});
            }
            return result;
        });
        const optionsJsonFilters = computed(() => {
            return optionsJson.value.filters;
        });
        const optionsFilters = computed(() => {
            return optionsJsonFilters.value.map(x => JSON.stringify(x));
        });


        let searchTimeout = null;
        const storeTitle = (item) => {
            if (!(item.id in componentData.hidden)) {
                componentData.hidden[item.id] = item;
            }
        };
        const getOptions = (tmpSearchValue = "") => {
            if (tmpSearchValue in componentData.results) {
                componentData.options = componentData.results[tmpSearchValue];
                componentData.loading = false;
                return;
            }

            const tmpObject = { data: [] };
            // Start showing options while loading instead.
            let tmpInterval = null;
            if (tmpSearchValue === "") {
                tmpInterval = setInterval(() => {
                    if (searchValue.value) {
                        tmpInterval && clearInterval(tmpInterval);
                        return;
                    }
                    if (tmpObject.data.length) {
                        tmpObject.data.forEach(storeTitle);
                        componentData.options = tmpObject.data;
                    }
                }, 200);
            }
            getDataWithoutLimit(`${apiHostname}/${config.value.workflowcard.url}`, optionsFilters.value, tmpObject, "data").then(() => {
                tmpObject.data.forEach(storeTitle);
                const tmpData = clone(tmpObject.data);
                componentData.options = tmpData;
                componentData.results[tmpSearchValue] = tmpData;
                componentData.loading = false;
                tmpInterval && clearInterval(tmpInterval);
            });
        };


        watch(
            () => searchValue.value,
            (newValue, oldValue) => {
                if (searchShowall.value && newValue === "") {
                    // Make sure to block any of this logic when showall is active
                    return;
                }
                componentData.loading = true;
                componentData.options = [];
                searchTimeout && clearTimeout(searchTimeout);
                searchTimeout = setTimeout(() => {
                    searchTimeout = null;
                    if (!newValue || newValue.length < 2) return;
                    searchShowall.value = false;
                    getOptions(newValue);
                }, 500);
            }
        );

        const searchShowall = ref(false);
        const searchSelection = ref(false);
        const searchAdding = ref(false);
        const searchFocus = ref(false);
        const show_dropdown = computed(() => {
            if (searchShowall.value) {
                return true;
            }
            if (searchValue.value && (searchFocus.value || searchSelection.value)) {
                return true;
            }
            return false;
        });
        watch(
            () => searchShowall.value,
            (showall) => {
                if (!showall) return;
                searchValue.value = "";
                getOptions("");
            }
        );

        const isOptionActive = (option) => {
            return computed(() => inputArray.value.includes(`${option.id}`)).value;
        };
        const handleOption = (option) => {
            if (isOptionActive(option)) {
                inputArray.value = inputArray.value.filter(a => `${a}` != `${option.id}`);
            } else {
                inputArray.value.push(`${option.id}`);
            }
            handleChange();
        };

        const handleSearchInput = (e) => {
            e.target.value = `${e.target.value.charAt(0).toUpperCase()}${e.target.value.slice(1)}`;
            searchValue.value = e.target.value;
        };
        const handleSearchReset = () => { searchValue.value = ""; };
        const handleSearchClick = (e) => { e.target.setSelectionRange(0, e.target.value.length); };
        const handleSearchFocus = (e) => {
            if (!editing.value) {
                return e.preventDefault();
            }
            searchFocus.value = true;
        };
        const handleSearchBlurSuspended = ref(false);
        const handleSearchBlur = () => {
            setTimeout(() => {
                if (handleSearchBlurSuspended.value) return;
                searchFocus.value = false;
                searchValue.value = "";
            }, 200);
        };
        const handleDropdownShowall = () => {
            searchTimeout && clearTimeout(searchTimeout);
            componentData.loading = true;
            searchShowall.value = true;
        };
        const handleDropdownHide = () => {
            searchFocus.value = searchSelection.value = false;
            searchShowall.value = false;
        };

        const handleSearchSelection = (option) => {
            handleOption(option);
        };

        // Keep original "hide" and "add" behaviors (only for options_object-backed sources)
        const handleSearchHide = (option) => {
            handleSearchBlurSuspended.value = true;
            searchRef.value?.focus();
            emit("value-hide", {
                name: props.name,
                value: option.id
            });
            setTimeout(() => { handleSearchBlurSuspended.value = false; }, 200);

            const formData = new FormData();
            formData.set("state", "Draft");
            patchDataAsync(`${apiHostname}/${config.value.workflowcard.url}/${option.id}`, formData).then(() => {
                delete componentData.options[option.id];
            });
        };
        const handleSearchAdd = () => {
            if (!searchValue.value || current_match.value) return;
            searchAdding.value = true;

            const formData = new FormData();
            formData.set("title", searchValue.value);
            postDataAsync(`${apiHostname}/${config.value.workflowcard.url}`, formData).then((result) => {
                if (result.data?.id) {
                    componentData.options[result.data.id] = resolveTitle(result.data) || result.data.title;
                    searchValue.value = "";
                    store_entity("workflowcard", result);
                    handleOption(result.data);
                }
                setTimeout(() => { searchAdding.value = false; }, 500);
            });
        };

        const filter = (options) => {
            return computed(() => {
                return Object.entries(options)
                    .filter(([key, value]) => !searchValue.value || value.toLowerCase().includes(searchValue.value?.toLowerCase()))
                    .sort((a, b) => a[1].localeCompare(b[1]))
                    .map(([id, title]) => ({ id, title }));
            }).value;
        };

        // --- readonly / killswitch observer (original)
        const readonly = computed(() => props.readonly);
        const editing = computed(() => !props.readonly && props.editing);

        const killswitch = inject("killswitch");
        const handleDropdownOutside = (e) => {
            if (e.target.closest(`.dropdown[data-name='${props.name}']`)) return;
            searchFocus.value = searchSelection.value = false;
        };
        onMounted(() => {
            if (props.readonly) {
                observeElement(inputRef.value, "value", (oldValue, newValue) => {
                    if (props.value == newValue) return;
                    console.error("DOM manipulation not allowed", oldValue, newValue);
                    killswitch && killswitch();
                });
            }
            document.addEventListener("click", handleDropdownOutside);
        });
        onUnmounted(() => {
            document.removeEventListener("click", handleDropdownOutside);
        });

        // --- drag & drop (original)
        let oldIndex = null;
        const isDragging = ref(false);
        const dragStart = (e) => {
            if (!editing.value) return;
            isDragging.value = true;
            oldIndex = e.currentTarget?.dataset?.key ?? e.target.dataset.key;
        };
        const allowDrop = (e) => { e.preventDefault(); };
        const drop = (e) => {
            e.preventDefault();
            const newIndex = e.currentTarget?.dataset?.key ?? e.target.dataset.key;
            const tmpArray = [...inputArray.value];
            tmpArray.splice(newIndex, 0, tmpArray.splice(oldIndex, 1)[0]);
            inputArray.value = tmpArray;
            isDragging.value = false;
        };

        const order = (data) => {
            return computed(() => {
                // Sort alphabetically
                return Object.values(data).sort((a, b) => {
                    return a.title.replace(/\s/g, "").localeCompare(b.title.replace(/\s/g, ""));
                });
            }).value;
        };

        return {
            trans,
            inputValue,
            inputArray,
            inputRef,
            searchRef,
            error,

            filter,
            order,
            componentData,
            resolveProps,
            validate,
            required,
            options,

            handleChange,

            searchValue,
            searchFocus,
            searchSelection,
            searchShowall,
            show_dropdown,

            isOptionActive,
            current_option,
            current_match,

            handleSearchInput,
            handleSearchClick,
            handleSearchFocus,
            handleSearchBlur,
            handleSearchReset,
            handleSearchSelection,
            handleSearchHide,
            handleSearchAdd,
            handleDropdownShowall,
            handleDropdownHide,

            handleOption,

            dragStart,
            allowDrop,
            drop,

            // exposed for tests/debug if needed
            usingFilterSource,

            readonly,
            editing
        };
    },
});
</script>
